// Settings → People. Add someone to Cipher, and see who is already in.
//
// WHY THIS SECTION EXISTS (Nick, 2026-10-29)
// ==========================================
// "I need you to provide a function in the settings where I can add users."
//
// This is function preservation, not new capability — the same principle as
// the PRESERVED table in `__tests__/settings.test.jsx`. The v1 app has had a
// founder-only **Operators** page at `/operators` since long before the v2
// shell: it invites, lists, and changes roles, and it is the only caller
// `POST /api/operators/invite` has ever had. Nothing in the v2 shell called
// it, so from inside v2 there was no way to add anybody, and Settings is
// where Nick went looking.
//
// `/operators` is NOT removed or unlinked by this (R2, R3) — it still
// resolves, it still works, and it still does the one thing this section
// deliberately does not: see "Changing someone later" at the foot of the
// file.
//
// WHAT HAPPENS WHEN YOU ADD SOMEONE
// =================================
// They are created with `status: "pending"` and nothing is emailed — Cipher
// has no invitation mail — so the form says to tell them yourself rather than
// promising a message that will never arrive. `routers/auth.py` flips pending
// to active on their first successful sign-in, Google or password. Their
// assistant is created in the same operation, dormant; only they can switch
// it on (Settings → My Executive Assistant).
//
// ROLE IS THE DANGEROUS FIELD, SO IT IS THE ONE SPELLED OUT
// =========================================================
// FOUNDER is not "admin with a nicer name": `require_founder` gates roughly
// three hundred endpoints, spend caps, the circuit breaker and service
// accounts among them. A chooser offering four words and explaining none of
// them invites the top one to be picked for convenience. Each role here
// carries the consequence of choosing it, and FOUNDER says plainly what it
// hands over.
//
// THE ROLES ARE THE BACKEND'S FOUR. `OperatorInvite.role` is a
// `Literal["FOUNDER", "OPERATOR", "MEDIA_BUYER", "VIDEO_EDITOR"]`; a fifth
// invented here would be a 422 the operator could not explain.
//
// THIS SCREEN GRANTS NOTHING ON ITS OWN. As everywhere else in the shell
// (R5), founder-gating here decides what is SHOWN. The server decides what is
// ALLOWED: a non-founder who types the URL gets the section frame and a
// refusal from the API, which is the honest outcome. `display: none` is not
// access control and neither is this.
import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

import { Block, Button, Gate, Row, Table, Value } from "../ui";
import { API, ENDPOINTS } from "../api";
import useResource from "../useResource";

/** The four roles `OperatorInvite` accepts, and what each one actually means. */
const ROLES = [
  ["OPERATOR", "Works across Cipher day to day. The ordinary choice."],
  ["MEDIA_BUYER", "Offers, campaigns and creative. No infrastructure."],
  ["VIDEO_EDITOR", "Creative production work only."],
  [
    "FOUNDER",
    "Everything you can do — spend caps, the circuit breaker, service "
      + "accounts and every founder-only screen. Give it only to someone who "
      + "should be able to spend money and change infrastructure.",
  ],
];

const BLANK = {
  name: "",
  email: "",
  operator_code: "",
  role: "OPERATOR",
  business_unit_ids: [],
};

const emptyList = (d) => !Array.isArray(d) || d.length === 0;

/**
 * The invite form.
 *
 * `axios` directly rather than the shared `post()` for the reason
 * `MySettings.jsx` gives for the same choice: this endpoint's refusals are
 * the useful part — "Email already registered", "Operator code already in
 * use", and the 503 that means no partial account was left behind — and the
 * shared helper collapses them into a generic message.
 */
function AddPerson({ units, onAdded }) {
  const [form, setForm] = useState(BLANK);
  const [state, setState] = useState("idle"); // idle | working | done | failed
  const [message, setMessage] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function toggleUnit(id) {
    setForm((f) => ({
      ...f,
      business_unit_ids: f.business_unit_ids.includes(id)
        ? f.business_unit_ids.filter((x) => x !== id)
        : [...f.business_unit_ids, id],
    }));
  }

  const code = form.operator_code.trim().toUpperCase();
  const ready =
    form.name.trim() && form.email.trim().includes("@") && code && state !== "working";

  async function submit() {
    setState("working");
    setMessage("");
    try {
      const res = await axios.post(
        `${API}${ENDPOINTS.operatorInvite}`,
        {
          email: form.email.trim(),
          name: form.name.trim(),
          operator_code: code,
          role: form.role,
          business_unit_ids: form.business_unit_ids,
        },
        { withCredentials: true }
      );
      setState("done");
      setMessage(
        `${form.email.trim()} now has a ${form.role} account `
          + `(${res.data?.operator_code || code}). Tell them to sign in — `
          + "the account goes live the first time they do."
      );
      setForm(BLANK);
      if (onAdded) onAdded();
    } catch (err) {
      setState("failed");
      setMessage(
        err.response?.data?.detail || err.message || "The account was not created."
      );
    }
  }

  const role = ROLES.find(([r]) => r === form.role);

  return (
    <Block
      title="Add someone to Cipher"
      note="Creates their account and their assistant together. Nobody is emailed — Cipher has no invitation mail — so send them the address yourself."
    >
      <div>
        <Row label="Name" hint="As it should appear in Cipher.">
          <input
            className="set-input"
            value={form.name}
            onChange={set("name")}
            autoComplete="off"
          />
        </Row>
        <Row label="Email" hint="The address they will sign in with.">
          <input
            className="set-input"
            type="email"
            value={form.email}
            onChange={set("email")}
            autoComplete="off"
          />
        </Row>
        <Row label="Short code" hint="Their initials or first name. Must be unique.">
          <input
            className="set-input"
            value={form.operator_code}
            onChange={(e) =>
              setForm((f) => ({ ...f, operator_code: e.target.value.toUpperCase() }))
            }
            autoComplete="off"
          />
        </Row>
        <Row label="Role" hint={role ? role[1] : ""}>
          <select className="set-input" value={form.role} onChange={set("role")}>
            {ROLES.map(([r]) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Row>
        <Row
          label="Workstreams"
          hint="Which sides of the business they are assigned to. Read from the business units that exist, not a fixed list."
        >
          <span className="set-inline">
            {units.length === 0 ? (
              <span className="dim">No business units to assign</span>
            ) : (
              units.map((u) => (
                <label className="set-toggle" key={u.business_unit_id}>
                  <input
                    type="checkbox"
                    checked={form.business_unit_ids.includes(u.business_unit_id)}
                    onChange={() => toggleUnit(u.business_unit_id)}
                  />
                  <span className="set-toggle-text">{u.name}</span>
                </label>
              ))
            )}
          </span>
        </Row>

        {form.role === "FOUNDER" ? (
          <p className="set-note dim" role="status">
            FOUNDER gives this person the same reach you have, including spend
            caps, the circuit breaker and service accounts.
          </p>
        ) : null}

        <span className="set-inline">
          <Button onClick={submit} disabled={!ready}>
            {state === "working" ? "Creating…" : "Create account"}
          </Button>
          {state === "done" ? (
            <span className="dim" role="status">
              {message}
            </span>
          ) : null}
          {state === "failed" ? (
            <span className="dim" role="alert">
              {message}
            </span>
          ) : null}
        </span>
      </div>
    </Block>
  );
}

/** Everyone who has an account, and whether they have ever signed in. */
function Directory({ operators, units }) {
  const unitName = (id) =>
    (units.find((u) => u.business_unit_id === id) || {}).name || id;

  return (
    <Block
      title="Who is in Cipher"
      note="Every account, what it can reach, and whether it has been used yet."
    >
      <Gate resource={operators} empty="No accounts yet.">
        {(rows) => (
          <Table head={["Name", "Email", "Role", "Workstreams", "Status"]}>
            {rows.map((o) => (
              <tr key={o.operator_id || o.email}>
                <td>{o.name}</td>
                <td className="dim">{o.email}</td>
                <td>{o.role}</td>
                <td className="dim">
                  {(o.business_unit_ids || []).map(unitName).join(", ") || "—"}
                </td>
                <td className="dim">
                  {o.status === "pending" ? "Invited — not signed in yet" : o.status}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Gate>
    </Block>
  );
}

export default function People() {
  const operators = useResource(ENDPOINTS.operators, { isEmpty: emptyList });
  const units = useResource(ENDPOINTS.businessUnits, { isEmpty: emptyList });
  // An empty or refused business-unit read must not block the form: the
  // assignment is optional on the backend, so the form degrades to "no
  // business units to assign" rather than disappearing.
  const unitRows = Array.isArray(units.data) ? units.data : [];

  return (
    <>
      <AddPerson units={unitRows} onAdded={operators.reload} />
      <Directory operators={operators} units={unitRows} />
      <Block
        title="Changing someone later"
        note="Changing a role is a different act from creating an account, and it already has a home. This section does not duplicate it."
      >
        <Row
          label="Role and workstreams"
          hint="The older Operators page does this today, and the server enforces its own limits there — the last founder cannot be demoted, and JC's role is hard-blocked."
        >
          <Value>
            <Link className="btn-ghost btn-sm" to="/operators">
              Open Operators
            </Link>
          </Value>
        </Row>
      </Block>
    </>
  );
}
